import { Content } from 'src/models/Content'
import { PromisedType } from 'src/utils/types'
import useSWR, { SWRConfiguration } from 'swr'
import { fetcher } from './client'
import {
  GET_CONTENTS,
  getContent,
  getContentTags,
  getSearchContents,
} from './endpoints'
import {
  ContentTagsResponse,
  SearchContentsResponse,
  ServerContent,
} from './types/content'

export type ContentsData = {
  newContents: Content[]
  bookmarkedContents: Content[]
  popularContents: Content[]
  weeklyContents: Content[]
}

const contentsFetcher = async (url: string) => {
  const data = await fetcher(url)
  return {
    newContents: data.newContents?.map(toContent) || [],
    bookmarkedContents: data.bookmarkedContents?.map(toContent) || [],
    popularContents: data.popularContents?.map(toContent) || [],
    weeklyContents: data.weeklyContents?.map(toContent) || [],
  }
}

export const useContents = () => {
  return useSWR<ContentsData>(GET_CONTENTS, contentsFetcher)
}

const allContentsFetcher = async (url: string) => {
  const data = await fetcher(url)
  return data.newContents?.map(toContent) || []
}

export const useAllContents = (config: Partial<SWRConfiguration> = {}) =>
  useSWR<Content[]>(GET_CONTENTS, allContentsFetcher, config)

const contentTagsFetcher = async (url: string) => {
  const data: ContentTagsResponse = await fetcher(url)
  return data.tags || []
}

export const useContentTags = (
  category?: string,
  config: Partial<SWRConfiguration> = {},
) => {
  return useSWR<ContentTagsResponse['tags']>(
    category !== undefined ? getContentTags(category) : null,
    contentTagsFetcher,
    config,
  )
}

export const useContent = (id: number, previewToken?: string) => {
  return useSWR<Content>(getContent(id, previewToken), async (url) => {
    const { content } = await fetcher(url)
    return toContent(content)
  })
}

export const searchContents = async (query: string) => {
  const data = await fetcher(
    `${getSearchContents([])}&query=${encodeURIComponent(query)}`,
  )
  return data?.contents?.map(toContent) || []
}

const toContent = (data: ServerContent): Content => ({
  id: data.id,
  title: data.title,
  body: data.body,
  byAdmin: data.byAdmin || false,
  imageUrl: data.image?.url || '',
  published: data.published || false,
  publishAt: data.publishAt || '',
  companyId: data.companyId || 0,
  companyName: data.company?.name || '',
  companyAdminName: data.company?.adminName || '',
  companyImageUrl:
    data.company?.imageUrl?.thumb?.url || data.company?.imageUrl?.url || '',
  companyRole: data.company?.role || '',
  isLiked: data.isLiked || false,
  isBookmarked: data.isBookmarked || false,
  likesCount: data.likesCount || 0,
  viewCount: data.viewCount || 0,
  isRequested: data.isRequested || false,
  contentTags:
    data.contentTags?.map((tag) => ({
      id: tag.id,
      name: tag.name,
      genre: tag.genre,
    })) || [],
})

const searchContentsFetcher = async (url: string) => {
  const res: SearchContentsResponse = await fetcher(url)
  return {
    contents: res.contents.map(toContent),
    totalCount: res.totalCount,
    currentPage: res.currentPage,
    totalPages: res.totalPages,
    perPage: res.perPage,
  }
}
export const useSearchContents = (
  tagIds?: number[],
  orderBy?: string,
  filterBy?: string,
  keyword?: string,
  category?: string,
  pageIndex: number = 1,
  perPage: number = 15,
  config: Partial<SWRConfiguration> = {},
) =>
  useSWR<PromisedType<typeof searchContentsFetcher>>(
    getSearchContents(
      tagIds,
      orderBy,
      filterBy,
      keyword,
      category,
      pageIndex,
      perPage,
    ),
    searchContentsFetcher,
    config,
  )
